import React, { useState } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { HelpCircle, Home, Settings, Map } from 'react-feather';
import { useMediaQuery } from 'react-responsive';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { useGeneralContext, useAuthContext } from '../../context';
import { DESKTOP_NAV } from '../../breakpoints';
import logo from '../../resources/dark-logo.png';
import Logo from '../Logo';
import LogoutButton from '../Logout';
import PremiumUpsellModal from '../PremiumUpsellModal';
import { getMenuItems } from './data';

type NavType = 'sideNav' | 'topNav' | 'topNavPrivate' | 'topNavPublic';
type NavRule = { largeScreen: NavType; smallScreen: NavType };

const APP_NAV: NavRule = {
  largeScreen: 'sideNav',
  smallScreen: 'topNavPrivate'
};

const PUBLIC_NAV: NavRule = {
  largeScreen: 'topNavPublic',
  smallScreen: 'topNav'
};

const navRules: { [path: string]: NavRule } = {
  '/': PUBLIC_NAV,
  '/about': PUBLIC_NAV,
  '/faq': PUBLIC_NAV,
  '/terms-and-privacy': PUBLIC_NAV,
  '/home': { ...APP_NAV },
  '/settings': APP_NAV,
  '/retrospective': APP_NAV
};

const appPaths = ['/home', '/settings', '/retrospective'];

// One shared shape for every side nav row, so the rows in the two groups can
// never drift apart. Height comes from padding plus a 44px floor rather than a
// fixed 51px, which used to clip any label that wrapped to a second line.
const SIDE_NAV_ROW =
  'tw-min-h-touch-min tw-py-3 tw-px-4 tw-flex tw-items-center tw-gap-3 tw-self-stretch tw-rounded-lg ' +
  'tw-text-base tw-text-left tw-no-underline tw-text-neutral-800 hover:tw-bg-white/60 hover:tw-text-neutral-800 ' +
  'tw-transition-colors';

const Navigation = () => {
  const { setFeedbackModalOpen, setPlansModalOpen } = useGeneralContext();
  const history = useHistory();

  const path = window.location.pathname;
  const loggedIn = appPaths.includes(path);

  const screenSize = useMediaQuery(DESKTOP_NAV) ? 'largeScreen' : 'smallScreen';
  const navComponentToUse = navRules[path][screenSize];

  const menuItems = getMenuItems({ setFeedbackModalOpen, setPlansModalOpen, history, loggedIn: loggedIn });

  if (navComponentToUse === 'sideNav') {
    return <SideNav />;
  }
  if (navComponentToUse === 'topNav') {
    return <TopNav loggedIn={loggedIn} menuItems={menuItems} />;
  }
  if (navComponentToUse === 'topNavPrivate') {
    return <TopNavPrivate menuItems={menuItems} />;
  }
  // topNavPublic
  return <LandingPageLargeScreenTopNav menuItems={menuItems} />;
};

const SideNav = () => {
  const context = useGeneralContext();
  const location = useLocation();
  const { nickname, feedbackModalOpen, setFeedbackModalOpen, subscriptionStatus } = context;
  const history = useHistory();

  const active = (fragment: string) =>
    location.pathname.includes(fragment) ? 'tw-bg-white tw-font-medium tw-shadow-sm' : '';

  return (
    // Was `sticky top-0 left-0 fixed` — two positioning schemes on one element.
    // Sticky is the one that was winning, so that is the one that stays.
    <div className="tw-w-sidenav tw-flex tw-flex-col tw-justify-between tw-sticky tw-top-0 tw-h-screen tw-overflow-y-auto">
      <div className="tw-flex tw-flex-col tw-items-stretch tw-gap-10 tw-flex-1 tw-px-6 tw-pt-12">
        <Logo style={{ width: '100px' }} includeTitle={true} />
        <nav className="tw-flex tw-flex-col tw-items-stretch tw-gap-1">
          <Link to="/home" className={`${SIDE_NAV_ROW} ${active('/home')}`}>
            <Home className="tw-w-5 tw-h-5 tw-shrink-0 tw-text-primary-500" />
            <span>Home</span>
          </Link>
          <Link
            to="#"
            className={`${SIDE_NAV_ROW} ${active('/retrospective')}`}
            onClick={() => {
              history.push('/retrospective');
            }}
          >
            <Map className="tw-w-5 tw-h-5 tw-shrink-0 tw-text-primary-500" />
            <span>Explore your Journal</span>
          </Link>
        </nav>
      </div>
      <div className="tw-flex tw-flex-col tw-items-stretch tw-px-6 tw-pb-10">
        <div className="tw-flex tw-flex-col tw-items-stretch tw-gap-1 tw-border-t tw-border-b tw-border-neutral-300 tw-py-3">
          <button
            onClick={() => {
              setFeedbackModalOpen(true);
            }}
            className={`${SIDE_NAV_ROW} tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-primary-800 ${
              feedbackModalOpen ? 'tw-bg-white tw-font-medium tw-shadow-sm' : ''
            }`}
          >
            <HelpCircle className="tw-w-5 tw-h-5 tw-shrink-0 tw-text-primary-800" />
            <span>Chat with us</span>
          </button>
          <Link to="/settings" className={`${SIDE_NAV_ROW} tw-text-primary-800 ${active('/settings')}`}>
            <Settings className="tw-w-5 tw-h-5 tw-shrink-0 tw-text-primary-800" />
            <span>Settings</span>
          </Link>
        </div>
        <div className="tw-mt-4 tw-flex tw-gap-3 tw-justify-between tw-items-center tw-px-4">
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-0.5 tw-min-w-0">
            <div className="tw-text-sm tw-font-semibold tw-truncate tw-max-w-full">{nickname}</div>
            <div className="tw-text-xs tw-text-neutral-500">{convertUserSubToString(subscriptionStatus)}</div>
          </div>
          <LogoutButton label="" />
        </div>
      </div>
    </div>
  );
};

const convertUserSubToString = (userSub: string | null) => {
  const UserSubToStringMap = {
    lifetime: 'Lifetime Plan',
    premium_monthly: 'Premium plan',
    premium_yearly: 'Premium plan',
    free_trial: 'Free trial',
    free: 'Free plan'
  };
  type UserSub = keyof typeof UserSubToStringMap;

  const isUserSub = (userSub: string): userSub is UserSub => {
    return Object.keys(UserSubToStringMap).includes(userSub);
  };

  return typeof userSub !== 'string' || !isUserSub(userSub) ? 'Free plan' : UserSubToStringMap[userSub];
};

type menuItem = {
  text: string;
  destination?: string;
  onClick?: () => void;
  icon?: any;
};

const TopNavPrivate = (props: { menuItems: menuItem[] }) => {
  const { signOut } = useAuthContext();
  return <TopNav loggedIn={true} menuItems={props.menuItems} signOut={signOut} />;
};

// Every item in the collapsed mobile menu, so rows are a consistent height and
// clear the 44px tap target. They used to be py-2 (40px total).
const TOP_NAV_ROW =
  'tw-flex tw-items-center tw-gap-3 tw-min-h-touch-min tw-py-2 tw-px-3 tw-rounded-lg tw-text-base ' +
  'tw-no-underline tw-text-neutral-800 hover:tw-bg-neutral-50 hover:tw-text-neutral-800';

const TopNav = (props: { loggedIn: boolean; menuItems: menuItem[]; signOut?: any }) => {
  const history = useHistory();
  const [navOpen, setNavOpen] = useState(false);

  const { loggedIn, menuItems, signOut } = props;

  const handleToggle = () => {
    setNavOpen(!navOpen);
  };

  const handleClose = () => {
    setNavOpen(false);
  };

  return (
    <Navbar collapseOnSelect fixed="top" expand={false} bg="light" variant="light" className="tw-shadow-sm">
      <Container className="tw-px-4">
        {/* The logo used to live inside the collapse, so the entire mobile
            header was a lone hamburger on a white bar: no branding and no way
            back to the home page without opening the menu. */}
        <Navbar.Brand as={NavLink} to={loggedIn ? '/home' : '/'} className="tw-flex tw-items-center tw-py-2">
          <img className="tw-w-24 tw-h-auto" alt="Jumble" src={logo} />
        </Navbar.Brand>
        <Navbar.Toggle
          onClick={handleToggle}
          aria-label="Toggle navigation"
          className="tw-min-h-touch-min tw-min-w-touch-min"
        />
        <Navbar.Collapse in={navOpen}>
          <Nav className="tw-flex tw-flex-col tw-items-stretch tw-gap-1 tw-py-3">
            {menuItems.map((item, index) => {
              const dest = item.destination;
              return dest ? (
                <NavLink
                  isActive={() => history?.location?.pathname === item.destination}
                  className={TOP_NAV_ROW}
                  key={`nav-item-${index}`}
                  to={item.destination}
                  onClick={() => {
                    handleClose();
                    if (signOut && item.text === 'Log out') {
                      sessionStorage.removeItem('stripeCustomerId');
                      signOut();
                    }
                    item.onClick && item.onClick();
                  }}
                >
                  {item.icon}
                  {item.text}
                </NavLink>
              ) : (
                <button
                  onClick={() => {
                    item.onClick && item.onClick();
                    handleClose();
                  }}
                  className={`${TOP_NAV_ROW} tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-left`}
                  key={`nav-item-${index}`}
                >
                  {item.icon}
                  {item.text}
                </button>
              );
            })}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

const LandingPageLargeScreenTopNav = (props: { menuItems: menuItem[] }) => {
  const { menuItems } = props;
  const [showExploreBlockedModal, setShowExploreBlockedModal] = React.useState(false);
  const history = useHistory();

  const leftGroup = menuItems.slice(0, 4);
  const rightGroup = menuItems.slice(4, menuItems.length);

  return (
    <Navbar fixed="top" expand bg="light" variant="light" className="tw-shadow-sm">
      <PremiumUpsellModal
        showModal={showExploreBlockedModal}
        setShowModal={setShowExploreBlockedModal}
        message="Get access to journal exploration and chat with your past journal entries."
      />
      <Container className="tw-flex tw-items-center tw-gap-8 tw-px-6">
        <NavLink to="/" className="tw-flex tw-items-center tw-shrink-0">
          <img className="tw-w-32 tw-h-auto" alt="Jumble" src={logo} />
        </NavLink>
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-between tw-gap-6">
          <div className="tw-flex tw-items-center tw-gap-1">
            {leftGroup.map((item, index) => {
              // gap-2 on the row spaced the links from each other but nothing
              // separated an item's icon from its own label, so the landing nav
              // read as "(i)About  (?)FAQ".
              const content = (
                <>
                  {item.icon}
                  <span>{item.text}</span>
                </>
              );
              const className =
                'tw-flex tw-items-center tw-gap-2 tw-min-h-touch-min tw-py-2 tw-px-3 tw-rounded-lg tw-text-base ' +
                'tw-no-underline tw-text-neutral-800 hover:tw-bg-neutral-100 hover:tw-text-neutral-800';
              return item.destination ? (
                <NavLink
                  isActive={() => history?.location?.pathname === item.destination}
                  className={className}
                  key={`nav-item-${index}`}
                  to={item.destination}
                  onClick={() => {
                    item.onClick && item.onClick();
                  }}
                >
                  {content}
                </NavLink>
              ) : (
                <button
                  onClick={() => {
                    item.onClick && item.onClick();
                  }}
                  className={`${className} tw-bg-transparent tw-border-none tw-cursor-pointer`}
                  key={`nav-item-${index}`}
                >
                  {content}
                </button>
              );
            })}
          </div>
          <div className="tw-flex tw-items-center tw-gap-3">
            {rightGroup.map((item, index) => {
              const isSecondary = index === 0;
              return (
                <NavLink
                  isActive={() => history?.location?.pathname === item.destination}
                  className={`tw-flex tw-items-center tw-justify-center tw-gap-2 tw-min-h-touch-min tw-px-4 tw-py-2 tw-rounded-lg
                    tw-text-base tw-font-semibold tw-whitespace-nowrap tw-no-underline tw-border-2 ${
                      isSecondary
                        ? 'tw-bg-white tw-border-primary-600 tw-text-primary-700 hover:tw-text-primary-700 hover:tw-bg-primary-50'
                        : 'tw-bg-primary-500 tw-border-primary-500 tw-text-white hover:tw-text-white hover:tw-bg-primary-600'
                    }`}
                  key={`nav-item-${index}`}
                  to={item.destination}
                  onClick={() => {
                    item.onClick && item.onClick();
                  }}
                >
                  {index === 1 ? null : item.icon}
                  {item.text}
                </NavLink>
              );
            })}
          </div>
        </div>
      </Container>
    </Navbar>
  );
};

export default Navigation;
